<template>
  <div class="patient-operation-preview d-flex justify-center">
    <phone-frame>
      <div class="phone-content-inner">
        <!-- Header -->
        <div class="d-flex justify-space-between align-center px-3 py-2 border-bottom">
          <span class="text-caption">取消約診 | Dent&Co</span>
          <v-icon size="20" color="grey darken-1">mdi-close</v-icon>
        </div>

        <!-- 語言選擇條 -->
        <div class="lang-bar d-flex justify-end pa-2">
          <v-chip small color="white" class="text-caption">中文</v-chip>
        </div>

        <!-- 主標題 -->
        <div class="text-center text-h6 font-weight-medium tiffany-10--text py-4">
          取消約診
        </div>

        <!-- 預約資訊區塊 -->
        <v-card flat color="grey lighten-4" rounded="lg" class="mx-4 mb-3 pa-3">
          <div class="text-subtitle-2 font-weight-medium mb-2">預約資訊</div>

          <div class="d-flex align-start mb-2">
            <span class="mr-2">📍</span>
            <div>
              <div class="text-caption">{{ mockData.clinicName }}</div>
              <div class="text-caption grey--text">{{ mockData.clinicAddress }}</div>
            </div>
          </div>

          <div class="d-flex align-center mb-2">
            <span class="mr-2 text-body-2">👨‍⚕️</span>
            <span class="text-caption">{{ mockData.doctorName }}醫師</span>
          </div>

          <div class="d-flex align-start">
            <span class="mr-2 text-body-2">📅</span>
            <div>
              <div class="text-caption">{{ formattedDate }}</div>
              <div class="text-caption grey--text">{{ formattedTime }}</div>
            </div>
          </div>
        </v-card>

        <!-- 原因選取區塊 -->
        <div class="px-4 mb-3">
          <div class="text-subtitle-2 font-weight-medium mb-1">請問您無法前往的原因</div>
          <div class="text-caption error--text mb-2">*為保留您下次預約權益，請勿頻繁改約。</div>
          <v-select
            v-model="localReason"
            :items="reasons"
            item-text="text"
            item-value="text"
            placeholder="請選擇原因"
            outlined
            dense
            hide-details
            color="tiffany-10"
            class="preview-select"
          />
        </div>

        <!-- 聯繫選項區塊 -->
        <div class="px-4 mb-3">
          <div class="text-subtitle-2 font-weight-medium mb-1">請問是否需要與您聯繫改約其他時間？</div>
          <div class="text-caption error--text mb-2">*為保留您下次預約權益，建議您主動聯繫。</div>
          <v-select
            v-model="localContact"
            :items="contacts"
            item-text="text"
            item-value="text"
            placeholder="請選擇"
            outlined
            dense
            hide-details
            color="tiffany-10"
            class="preview-select"
          />
        </div>

        <!-- 按鈕區 -->
        <div class="px-4 mt-auto">
          <v-btn block color="tiffany-10" dark class="mb-2 rounded-lg">確認送出</v-btn>
          <v-btn block color="grey darken-2" dark class="mb-2 rounded-lg">我沒有要取消，點擊關閉頁面</v-btn>
        </div>

        <!-- Footer -->
        <div class="text-center text-caption grey--text pa-2">
          若遇到任何系統問題，請聯絡<span class="tiffany-10--text">系統客服</span>。
        </div>
      </div>
    </phone-frame>
  </div>
</template>

<script>
import PhoneFrame from './PhoneFrame.vue'

export default {
  name: 'PatientOperationPreview',

  components: {
    PhoneFrame
  },

  props: {
    mockData: {
      type: Object,
      default: () => ({})
    },
    reasons: {
      type: Array,
      default: () => []
    },
    contacts: {
      type: Array,
      default: () => []
    },
    selectedReason: {
      type: String,
      default: ''
    },
    selectedContact: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      localReason: '',
      localContact: ''
    }
  },

  computed: {
    formattedDate() {
      const date = this.mockData.appointmentDate || ''
      const weekday = this.mockData.appointmentWeekday || ''
      // 轉換格式：2026/01/20 -> 2026年01月20日 星期一
      if (date) {
        const parts = date.split('/')
        if (parts.length === 3) {
          return `${parts[0]}年${parts[1]}月${parts[2]}日 星期${weekday}`
        }
      }
      return date
    },
    formattedTime() {
      const time = this.mockData.appointmentTime || ''
      // 轉換格式：14:30 -> 下午2:30
      if (time) {
        const [hour, minute] = time.split(':')
        const h = parseInt(hour, 10)
        if (h >= 12) {
          return `下午${h === 12 ? 12 : h - 12}:${minute}`
        }
        return `上午${h}:${minute}`
      }
      return time
    }
  },

  watch: {
    selectedReason: {
      immediate: true,
      handler(val) {
        this.localReason = val
      }
    },
    selectedContact: {
      immediate: true,
      handler(val) {
        this.localContact = val
      }
    },
    localReason(val) {
      this.$emit('update:selectedReason', val)
    },
    localContact(val) {
      this.$emit('update:selectedContact', val)
    }
  }
}
</script>

<style scoped lang="scss">
.patient-operation-preview {
  width: 100%;
  max-width: 360px;
}

.phone-content-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 12px;
}

.lang-bar {
  background: var(--v-tiffany-10-base, #00D4B4);
}

.border-bottom {
  border-bottom: 1px solid #E5E7EB;
}

.preview-select {
  ::v-deep .v-input__slot {
    min-height: 36px !important;
    border-color: var(--v-tiffany-10-base, #00D4B4) !important;
    border-width: 2px !important;
    border-radius: 10px !important;
  }

  ::v-deep .v-select__selection {
    font-size: 12px;
  }

  ::v-deep .v-input__append-inner .v-icon {
    color: var(--v-tiffany-10-base, #00D4B4);
  }
}

</style>
